import { EventState, GlobalLotsUiConfigKeys } from 'types';
import { Global } from '@utils/common';

type Key = keyof GlobalLotsUiConfigKeys;

export const globalLotsUiConfigKeys: Record<Key, keyof EventState> = {
  collect_items_per_unit: 'collectItemsPerUnit',
  set_baseline_average_on_first_round_quotes: 'setBaselineAverage',
  show_discount: 'showDiscount',
  show_extended_price_feedback: 'showExtendedPriceFeedback',
  show_logistics_charge: 'showLogisticsCharge',
  show_pricing_columns: 'showPricingColumns',
};

const getPriceDependentKeys = (lotsUiConfigKeys: GlobalLotsUiConfigKeys): Key[] => ([
  // While we are not showing these options, we need to keep these as part of the logic
  // so we don't get them out of sync before GA'ing this feature
  lotsUiConfigKeys.show_logistics_charge,
  lotsUiConfigKeys.show_discount,
  lotsUiConfigKeys.collect_items_per_unit, // Collect Items Per Unit
  lotsUiConfigKeys.set_baseline_average_on_first_round_quotes // Use Intelligent Counter Offer
]);

const disabledDependentOptions = (dependentKeys: Key[]): Record<Key, boolean> => {
  const options: Record<Key, boolean> = {};

  dependentKeys.forEach((attr: Key): void => {
    options[attr] = false;
  });

  return options;
};

interface PrepareUiConfigChangesParams {
  changes: { key: Key; value: boolean };
  showPricingColumns: boolean;
}

export const prepareUiConfigChanges = ({
  changes,
  showPricingColumns,
}: PrepareUiConfigChangesParams): Record<Key, boolean> | undefined => {
  const lotsUiConfigKeys = Global?.lots?.ui_config_keys || {};
  const dependentKeys = getPriceDependentKeys(lotsUiConfigKeys);
  const { key, value } = changes;

  // Disabled states so don't apply any changes.
  // Any changes to this logic require changing the disabled prop on the menu items below.
  if (dependentKeys.includes(key) && !showPricingColumns) return undefined;

  // If Show Pricing Columns is being toggled OFF, we should toggle off price-dependent options
  if (key === lotsUiConfigKeys.show_pricing_columns && !value) {
    return { [key]: value, ...disabledDependentOptions(dependentKeys) };
  }

  return { [key]: value };
};
